import { useEffect, useState } from 'react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { api } from '../lib/api'
import clsx from 'clsx'

const INK = '#1a3a40'
const BRAND_TEAL = '#2A7A8A'
const BRAND_SAGE = '#B8C7B1'
const WARM_CARD = '#FDFCF8'
const WARM_BORDER = '#e0d9cc'

interface NudgeStat {
  id: string
  title: string
  category: string
  total_sent: number
  total_opened: number
  total_acted: number
  open_rate: number
  action_rate: number
}

const PERIOD_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
]

export function AnalyticsPage() {
  const [stats, setStats] = useState<NudgeStat[]>([])
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState(30)

  useEffect(() => {
    setLoading(true)
    api.getNudgeStats(period)
      .then(r => setStats((r as { stats: NudgeStat[] }).stats))
      .finally(() => setLoading(false))
  }, [period])

  const topNudges = stats.slice(0, 10)
  const avgActionRate = stats.length
    ? Math.round(stats.reduce((s, n) => s + n.action_rate, 0) / stats.length)
    : 0
  const totalSent = stats.reduce((s, n) => s + n.total_sent, 0)
  const totalActed = stats.reduce((s, n) => s + n.total_acted, 0)

  const barFill = (i: number) => (i < 3 ? BRAND_TEAL : BRAND_SAGE)

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-7">
        <div>
          <h1 className="text-2xl font-bold text-ink" style={{fontFamily: "'Playfair Display', Georgia, serif"}}>Analytics</h1>
          <p className="text-sm mt-1" style={{color: '#6b8a8f'}}>Nudge performance across your team</p>
        </div>
        <div className="flex gap-2">
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => setPeriod(opt.value)}
              className="px-3 py-1.5 rounded-full text-xs font-medium transition-colors border"
            style={period === opt.value ? {
              background: '#2A7A8A', color: '#fff', borderColor: '#2A7A8A',
              boxShadow: '0 0 20px -6px rgba(42,122,138,0.4)'
            } : {
              background: '#FDFCF8', color: '#6b8a8f', borderColor: '#e0d9cc'
            }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-8">
        {[
          { label: 'Nudges Sent', value: totalSent.toLocaleString() },
          { label: 'Actions Taken', value: totalActed.toLocaleString() },
          { label: 'Avg. Action Rate', value: `${avgActionRate}%` },
        ].map(({ label, value }, i) => (
          <div
            key={label}
            className={clsx(
              'rounded-2xl border p-5 shadow-card relative overflow-hidden',
              i === 0 && 'animate-breathe'
            )}
            style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
          >
            <div
              className={clsx(
                'absolute inset-0 pointer-events-none opacity-80',
                i % 2 === 0 ? 'glow-radial-teal' : 'glow-radial-sage'
              )}
              aria-hidden
            />
            <div className="relative">
              <p className="text-xs text-ink-soft mb-1">{label}</p>
              <p className="text-2xl font-bold text-ink">{loading ? '—' : value}</p>
            </div>
          </div>
        ))}
      </div>

      <div
        className="rounded-2xl border p-6 mb-6 shadow-card"
        style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
      >
        <h2 className="text-sm font-semibold text-ink mb-4">Top Nudges by Action Rate</h2>
        {loading ? (
          <div className="h-48 flex items-center justify-center text-ink-muted text-sm">Loading…</div>
        ) : topNudges.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-ink-muted text-sm">
            No data yet — nudges will appear here once delivered.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={topNudges} layout="vertical" margin={{ left: 8, right: 8 }}>
              <XAxis
                type="number"
                domain={[0, 100]}
                tickFormatter={v => `${v}%`}
                tick={{ fontSize: 11, fill: INK }}
                tickLine={false}
                axisLine={{ stroke: WARM_BORDER }}
              />
              <YAxis
                type="category"
                dataKey="title"
                tick={{ fontSize: 11, fill: INK }}
                tickLine={false}
                axisLine={{ stroke: WARM_BORDER }}
                width={160}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null
                  return (
                    <div
                      className="rounded-lg px-3 py-2 text-xs shadow-card border max-w-xs"
                      style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
                    >
                      <p className="text-ink font-medium mb-1">{String(label)}</p>
                      <p className="text-brand-teal font-semibold">
                        {(payload[0].value as number)}% action rate
                      </p>
                    </div>
                  )
                }}
              />
              <Bar dataKey="action_rate" radius={[0, 6, 6, 0]}>
                {topNudges.map((_, i) => (
                  <Cell key={i} fill={barFill(i)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div
        className="rounded-2xl border overflow-hidden shadow-card"
        style={{ background: WARM_CARD, borderColor: WARM_BORDER }}
      >
        <div className="px-6 py-4 border-b" style={{ borderColor: WARM_BORDER }}>
          <h2 className="text-sm font-semibold text-ink">All Nudges</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b" style={{ borderColor: WARM_BORDER }}>
                {['Nudge', 'Category', 'Sent', 'Opened', 'Acted', 'Action Rate'].map(h => (
                  <th key={h} className="text-left px-5 py-3 text-xs font-medium text-ink-soft">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-warm-border/60">
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="px-5 py-3">
                        <div className="h-3 bg-warm-inset rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
                : stats.map(n => (
                  <tr
                    key={n.id}
                    className="border-b border-warm-border/60 hover:bg-warm-inset/50 transition-colors"
                  >
                    <td className="px-5 py-3 text-ink font-medium max-w-[200px] truncate">{n.title}</td>
                    <td className="px-5 py-3 text-ink-muted capitalize">{n.category}</td>
                    <td className="px-5 py-3 text-ink-muted">{n.total_sent}</td>
                    <td className="px-5 py-3 text-ink-muted">{n.total_opened}</td>
                    <td className="px-5 py-3 text-ink-muted">{n.total_acted}</td>
                    <td className="px-5 py-3">
                      <RateBadge value={n.action_rate} />
                    </td>
                  </tr>
                ))
              }
              {!loading && stats.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-10 text-center text-ink-muted text-sm">
                    No data for this period.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function RateBadge({ value }: { value: number }) {
  const color =
    value >= 50 ? 'text-emerald-800' : value >= 25 ? 'text-amber-800' : 'text-ink-muted'
  return <span className={clsx('font-medium tabular-nums', color)}>{value ?? 0}%</span>
}
